import React, { useState, useRef, useCallback } from 'react';
import { MiddleWall } from '../types';

interface MiddleWallEditDialogProps {
  middleWall: MiddleWall;
  horizontal: number;
  vertical: number;
  panelThick: number;
  onConfirm: (mw: MiddleWall) => void;
  onDelete: (id: string) => void;
  onCancel: () => void;
}

const PANEL_VARIANTS: { value: 1|2|3|4; label: string; lt: string; rt: string }[] = [
  { value: 1, label: '1번', lt: '◁', rt: '▷' },
  { value: 2, label: '2번', lt: '▶', rt: '◀' },
  { value: 3, label: '3번', lt: '▶', rt: '▷' },
  { value: 4, label: '4번', lt: '◁', rt: '◀' },
];

const DIALOG_W = 360;

const MiddleWallEditDialog: React.FC<MiddleWallEditDialogProps> = ({
  middleWall, horizontal, vertical, panelThick, onConfirm, onDelete, onCancel,
}) => {
  const [direction, setDirection] = useState(middleWall.direction);
  const [panelVariant, setPanelVariant] = useState<1|2|3|4>(middleWall.panelVariant ?? 1);

  // 위치: 패널 안쪽 기준
  // vertical: 좌/우 선택 가능, horizontal: 상/하 선택 가능
  const innerH = horizontal - panelThick * 2;
  const innerV = vertical - panelThick * 2;

  const initPos = middleWall.position;
  const initFromOtherH = innerH - initPos;
  const initFromOtherV = innerV - initPos;

  const [hSide, setHSide] = useState<'left'|'right'>(
    initPos <= initFromOtherH ? 'left' : 'right'
  );
  const [vSide, setVSide] = useState<'top'|'bottom'>(
    initPos <= initFromOtherV ? 'top' : 'bottom'
  );
  const [posVal, setPosVal] = useState(
    middleWall.direction === 'vertical'
      ? (initPos <= initFromOtherH ? initPos : initFromOtherH)
      : (initPos <= initFromOtherV ? initPos : initFromOtherV)
  );

  const [dialogPos, setDialogPos] = useState({ x: 24, y: window.innerHeight - 560 - 80 });
  const dragging = useRef(false);
  const dragStart = useRef({ mx: 0, my: 0, px: 0, py: 0 });

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    dragging.current = true;
    dragStart.current = { mx: e.clientX, my: e.clientY, px: dialogPos.x, py: dialogPos.y };
    e.preventDefault();
  }, [dialogPos]);

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragging.current) return;
    setDialogPos({
      x: dragStart.current.px + (e.clientX - dragStart.current.mx),
      y: dragStart.current.py + (e.clientY - dragStart.current.my),
    });
  }, []);

  const onMouseUp = useCallback(() => { dragging.current = false; }, []);

  // 방향 변경: 예전 방향의 위치 값은 뜻이 달라지므로 새 방향 실내 가운데로 (원래 방향으로 되돌리면 원래 값)
  const handleDirectionChange = (dir: 'horizontal' | 'vertical') => {
    if (dir === direction) return;
    setDirection(dir);
    if (dir === middleWall.direction) {
      setPosVal(dir === 'vertical' ? (initPos <= initFromOtherH ? initPos : initFromOtherH) : (initPos <= initFromOtherV ? initPos : initFromOtherV));
      setHSide(initPos <= initFromOtherH ? 'left' : 'right'); setVSide(initPos <= initFromOtherV ? 'top' : 'bottom');
    } else {
      setPosVal(Math.round((dir === 'vertical' ? innerH : innerV) / 2 / 10) * 10);
      if (dir === 'vertical') setHSide('left'); else setVSide('top');
    }
  };

  const getPosition = (): number => {
    if (direction === 'vertical') {
      return hSide === 'left' ? posVal : innerH - posVal;
    } else {
      return vSide === 'top' ? posVal : innerV - posVal;
    }
  };

  const handleConfirm = () => {
    const position = getPosition();
    const maxPos = direction === 'vertical' ? innerH : innerV;
    if (position < 0 || position > maxPos) {
      alert('칸막이 위치가 도면을 벗어납니다.');
      return;
    }
    const length = direction === 'vertical'
      ? vertical - panelThick * 2
      : horizontal - panelThick * 2;
    // 방향을 바꾸면 기준점(실내 구간 고르기)은 원래 칸막이 중심선 자리 — ㄷ자 등에서 같은 방에 놓이게
    const anchor = direction === middleWall.direction ? middleWall.anchor : panelThick + middleWall.position;
    onConfirm({ ...middleWall, direction, position, length, panelVariant, anchor });
  };

  const innerLabel = direction === 'vertical'
    ? `내부 가로: ${innerH}mm`
    : `내부 세로: ${innerV}mm`;

  return (
    <div
      style={{
        position: 'fixed', left: dialogPos.x, top: dialogPos.y, width: DIALOG_W, zIndex: 1000,
        background: '#fff', borderRadius: 20, boxShadow: '0 12px 40px rgba(0,0,0,0.16)', overflow: 'hidden', userSelect: 'none',
      }}
      onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
    >
      <div onMouseDown={onMouseDown} style={{
        background: '#fff', color: '#191F28', padding: '16px 20px 8px',
        borderRadius: 0, cursor: 'grab',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{ fontWeight: 'bold', fontSize: 14 }}>칸막이 수정</span>
        <button onClick={onCancel} style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: '#F2F4F6', color: '#4E5968', fontSize: 14, cursor: 'pointer', flexShrink: 0 }}>✕</button>
      </div>

      <div style={{ padding: 16 }}>

        {/* 방향 */}
        <div style={fieldStyle}>
          <label style={dlabelStyle}>방향</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => handleDirectionChange('vertical')} style={{
              flex: 1, padding: '8px 4px', cursor: 'pointer', borderRadius: 6,
              border: direction === 'vertical' ? '2px solid #3182F6' : '1px solid #E5E8EB',
              background: direction === 'vertical' ? '#E8F3FF' : '#fff',
              fontSize: 18,
            }}>┃ 세로</button>
            <button onClick={() => handleDirectionChange('horizontal')} style={{
              flex: 1, padding: '8px 4px', cursor: 'pointer', borderRadius: 6,
              border: direction === 'horizontal' ? '2px solid #3182F6' : '1px solid #E5E8EB',
              background: direction === 'horizontal' ? '#E8F3FF' : '#fff',
              fontSize: 18,
            }}>━ 가로</button>
          </div>
        </div>

        {/* 위치 */}
        <div style={fieldStyle}>
          <label style={dlabelStyle}>위치 (패널 안쪽 기준, mm)</label>
          {direction === 'vertical' ? (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button onClick={() => setHSide('left')} style={sideBtnStyle(hSide === 'left')}>좌측 벽</button>
              <button onClick={() => setHSide('right')} style={sideBtnStyle(hSide === 'right')}>우측 벽</button>
              <input type="number" value={posVal} onChange={e => setPosVal(Number(e.target.value))}
                style={{ ...dinputStyle, flex: 1 }} step={10} />
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button onClick={() => setVSide('top')} style={sideBtnStyle(vSide === 'top')}>상단 벽</button>
              <button onClick={() => setVSide('bottom')} style={sideBtnStyle(vSide === 'bottom')}>하단 벽</button>
              <input type="number" value={posVal} onChange={e => setPosVal(Number(e.target.value))}
                style={{ ...dinputStyle, flex: 1 }} step={10} />
            </div>
          )}
          <span style={hintStyle}>{innerLabel}</span>
        </div>

        {/* 패널 음양수 */}
        <div style={fieldStyle}>
          <label style={dlabelStyle}>패널 음양수 타입</label>
          <div style={{ display: 'flex', gap: 6 }}>
            {PANEL_VARIANTS.map(pv => (
              <button key={pv.value} onClick={() => setPanelVariant(pv.value)} style={{
                flex: 1, padding: '8px 4px', cursor: 'pointer', borderRadius: 6,
                border: panelVariant === pv.value ? '2px solid #3182F6' : '1px solid #E5E8EB',
                background: panelVariant === pv.value ? '#E8F3FF' : '#fff',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
              }}>
                <span style={{ fontSize: 11, fontWeight: 'bold', color: '#333' }}>{pv.label}</span>
                <span style={{ fontSize: 11, color: '#555', letterSpacing: 2 }}>{pv.lt} {pv.rt}</span>
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 16 }}>
          <button onClick={() => {
            if (window.confirm('칸막이를 삭제하시겠습니까?')) onDelete(middleWall.id);
          }} style={deleteBtnStyle}>삭제</button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onCancel} style={cancelBtnStyle}>취소</button>
            <button onClick={handleConfirm} style={confirmBtnStyle}>적용</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const sideBtnStyle = (active: boolean): React.CSSProperties => ({
  padding: '4px 10px', fontSize: 11, cursor: 'pointer', borderRadius: 8,
  border: active ? '2px solid #3182F6' : '1px solid #E5E8EB',
  background: active ? '#E8F3FF' : '#fff',
  color: active ? '#3182F6' : '#666',
  fontWeight: active ? 'bold' : 'normal',
  whiteSpace: 'nowrap',
});

const fieldStyle: React.CSSProperties = { marginBottom: 12 };
const dlabelStyle: React.CSSProperties = { display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 'bold', color: '#555' };
const dinputStyle: React.CSSProperties = { width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid #E5E8EB', borderRadius: 8, boxSizing: 'border-box' };
const hintStyle: React.CSSProperties = { fontSize: 11, color: '#999', marginTop: 3, display: 'block' };
const deleteBtnStyle: React.CSSProperties = { height: 38, padding: '0 16px', fontSize: 14, fontWeight: 600, border: 'none', borderRadius: 10, background: '#FFEEF0', color: '#F04452', cursor: 'pointer' };
const cancelBtnStyle: React.CSSProperties = { height: 38, padding: '0 16px', fontSize: 14, fontWeight: 600, border: 'none', borderRadius: 10, background: '#F2F4F6', color: '#333D4B', cursor: 'pointer' };
const confirmBtnStyle: React.CSSProperties = { height: 38, padding: '0 18px', fontSize: 14, fontWeight: 600, border: 'none', borderRadius: 10, background: '#3182F6', color: '#fff', cursor: 'pointer' };

export default MiddleWallEditDialog;
